import isEqual from 'lodash/isEqual'
import { useMemo } from 'react'
import {
  DataFieldComponent,
  FilterOperator,
  type FieldValues
} from '../../../types'
import { useFieldContext } from '../FieldContext'
import { useDataFieldModel } from '../useDataFieldModel'
import DataFieldDatePicker from './DataFieldDatePicker'
import {
  DataFieldRelationMultiselection,
  DataFieldRelationUniselection
} from './DataFieldRelationAutocomplete'
import DataFieldStandardSelection from './DataFieldStandardSelection'
import DataFieldUnifree from './DataFieldUnifree'
import type { DataFieldProps } from './types'

const DataField = ({ inputRef, parentRef }: DataFieldProps) => {
  const {
    filterField,
    fieldLayout,
    fieldValues,
    handleChange,
    uuidCurrentSitemodelObjectclass,
    groupedFieldValues,
    interdependentFilteringQuery,
    disabled: disabledFromContext,
    dateFormat = 'date-time',
    groupPosition,
    fieldConcatenation,
    selectAndLinkTargetPath,
    changedFilteractionData
  } = useFieldContext()

  const {
    id,
    name = '',
    label: defaultLabel,
    type,
    objectclassUuid = ''
  } = filterField
  const data = name ? fieldValues[name] : undefined
  const operator = name ? fieldValues[name]?.operator : undefined

  const onChangeValues = (value: FieldValues['']['value']) => {
    if (!name || !data) return
    handleChange?.({
      [name]: {
        ...data,
        value
      }
    })
  }

  const isValid = useMemo(
    () => Boolean(data && isEqual(changedFilteractionData, data)),
    [data, changedFilteractionData]
  )

  const { label, commonProps, component, filteraction, displayActions } =
    useDataFieldModel({
      fieldLayout,
      type,
      name,
      id,
      defaultLabel,
      disabled: disabledFromContext
    })

  if (!component || !data) {
    return null
  }

  if (component === DataFieldComponent.DatePicker) {
    return (
      <DataFieldDatePicker
        commonProps={commonProps}
        inputRef={inputRef}
        value={data.value as string}
        onChangeValues={onChangeValues}
        dateFormat={dateFormat}
        isValid={isValid}
      />
    )
  }

  if (
    component === DataFieldComponent.Unifree ||
    operator === FilterOperator.Filteraction
  ) {
    return (
      <DataFieldUnifree
        commonProps={commonProps}
        inputRef={inputRef}
        value={data.value as string}
        onChangeValues={onChangeValues}
        isValid={isValid}
      />
    )
  }

  if (component === DataFieldComponent.AutocompleteMultiselection) {
    return (
      <DataFieldRelationMultiselection
        commonProps={commonProps}
        name={name}
        label={label}
        objectclassUuid={objectclassUuid}
        groupedFieldValues={groupedFieldValues}
        displayActions={displayActions}
        groupPosition={groupPosition}
        dataValue={data.value}
        selectAndLinkTargetPath={selectAndLinkTargetPath}
        inputRef={inputRef}
        parentRef={parentRef}
        onChangeValues={onChangeValues}
        interdependentFilteringQuery={interdependentFilteringQuery}
        fieldConcatenation={fieldConcatenation}
        isValid={isValid}
      />
    )
  }

  if (component === DataFieldComponent.AutocompleteUniselection) {
    return (
      <DataFieldRelationUniselection
        commonProps={commonProps}
        name={name}
        objectclassUuid={objectclassUuid}
        groupedFieldValues={groupedFieldValues}
        displayActions={displayActions}
        groupPosition={groupPosition}
        dataValue={data.value}
        inputRef={inputRef}
        onChangeValues={onChangeValues}
        uuidCurrentSitemodelObjectclass={uuidCurrentSitemodelObjectclass}
        fieldConcatenation={fieldConcatenation}
        isValid={isValid}
      />
    )
  }

  if (component === DataFieldComponent.StandardUniselection) {
    return (
      <DataFieldStandardSelection
        variant='uniselection'
        commonProps={commonProps}
        filteraction={filteraction}
        inputRef={inputRef}
        onChangeValues={onChangeValues}
        objectclassUuid={objectclassUuid}
        uuidCurrentSitemodelObjectclass={uuidCurrentSitemodelObjectclass}
        selectedOptions={data.value}
        isValid={isValid}
      />
    )
  }

  if (component === DataFieldComponent.StandardMultiselection) {
    return (
      <DataFieldStandardSelection
        variant='multiselection'
        commonProps={commonProps}
        filteraction={filteraction}
        inputRef={inputRef}
        onChangeValues={onChangeValues}
        objectclassUuid={objectclassUuid}
        uuidCurrentSitemodelObjectclass={uuidCurrentSitemodelObjectclass}
        selectedOptions={data.value}
        isValid={isValid}
      />
    )
  }

  return null
}

export default DataField
